import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface EvidenceContextValue {
  activeId: string | null;
  setActive: (id: string | null) => void;
  scrollTo: (id: string) => void;
}

const EvidenceContext = createContext<EvidenceContextValue | null>(null);

export function EvidenceProvider({ children }: { children: ReactNode }): JSX.Element {
  const [activeId, setActiveId] = useState<string | null>(null);

  const setActive = useCallback((id: string | null) => {
    setActiveId(id);
    if (id) {
      // Auto-clear the highlight after the flash so a second click on the
      // same badge re-flashes (without this, the activeId stays "set" and
      // the CSS transition only fires once).
      setTimeout(() => setActiveId((current) => (current === id ? null : current)), 1500);
    }
  }, []);

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(`ev-${id}`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, []);

  const value = useMemo<EvidenceContextValue>(
    () => ({ activeId, setActive, scrollTo }),
    [activeId, setActive, scrollTo],
  );

  return <EvidenceContext.Provider value={value}>{children}</EvidenceContext.Provider>;
}

export function useEvidence(): EvidenceContextValue {
  const ctx = useContext(EvidenceContext);
  if (!ctx) {
    // Allow components to render outside the provider (e.g. used standalone in
    // a list view). They get no-op handlers — clicks become inert, no crash.
    return { activeId: null, setActive: () => {}, scrollTo: () => {} };
  }
  return ctx;
}

// Helper for evidence-bearing item containers — sets id + applies the flash
// class when the evidence id matches the active one.
export function evidenceTargetProps(id: string | undefined, activeId: string | null): { id?: string; className: string } {
  if (!id) return { className: '' };
  return {
    id: `ev-${id}`,
    className: activeId === id ? 'bg-amber-100 transition-colors duration-700' : 'transition-colors duration-700',
  };
}
